// ============================================================
// v5 P1 — client Web Push subscription. Requests Notification
// permission (in the lobby, after location — §17.1) and registers
// a PushSubscription with the server's VAPID public key. The
// service worker (src/sw.ts) shows the notifications.
// ============================================================

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

/** Subscribe (requesting permission if needed) and hand the subscription to the
 *  caller to send to the server. Safe to call repeatedly (reuses any existing
 *  subscription). Returns false if unsupported / denied / no key. */
export async function subscribePush(
  vapidPublicKey: string | null,
  onSubscription: (sub: unknown) => void,
  opts: { prompt?: boolean } = {},
): Promise<boolean> {
  if (!pushSupported() || !vapidPublicKey) return false;
  if (Notification.permission === 'denied') return false;
  if (Notification.permission !== 'granted') {
    if (!opts.prompt) return false; // don't nag — only on an explicit gesture
    const p = await Notification.requestPermission();
    if (p !== 'granted') return false;
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      }));
    onSubscription(sub.toJSON());
    return true;
  } catch {
    return false;
  }
}
